import { useState } from 'react';
import type { SupplyProduct } from '@obraplan/core/application/ports/requisition';
import type {
  CatalogItem,
  CatalogService,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';
import { cn } from '@/lib/cn';
import { BuscaNoCompras, type CatalogoIndisponivel } from './BuscaNoCompras';
import { itensParaPropagarCodigo, outroItemComCodigo } from './codigo-de-compras';
import {
  compararUnidades,
  periodoDeDozeMeses,
  textoDoPrecoMedio,
  unidadeNaMedia,
} from './preco-do-compras';
import type { WorkspaceAction } from './typology-draft';

/** O código que a pessoa escolheu, antes de aplicar. */
interface CodigoEscolhido {
  readonly code: string;
  /** O nome do produto no compras — ausente no código digitado. */
  readonly description?: string;
  /** A unidade do produto no compras, quando a busca a trouxe. */
  readonly unit?: string;
  /**
   * Escolhido da busca: o compras disse que o SKU existe. O digitado com o compras fora do
   * alcance não foi conferido por ninguém (D9).
   *
   * **Não é guardado no item**: ele só tem `code`, e a marca vive enquanto o diálogo está
   * aberto. Quem guarda o que o compras disse é a foto do preço (etapa 2, D5, RN-397): o
   * código conferido a tira na hora (`onCodigoConferido`); o não conferido espera o
   * "Atualizar preços" — perguntar agora ao compras que acabou de não responder seria só uma
   * segunda falha.
   */
  readonly conferido: boolean;
}

/**
 * O código de compras de um item que já existe (Fase 29, RN-395) — "Informar código" ou
 * "Trocar código", ao lado do código na linha do item.
 *
 * Antes, o código só nascia com o item: o "Novo item" buscava no compras, e depois disso a
 * linha só o mostrava. A planilha do orçamento traz centenas de itens sem código, e o
 * orçamento da obra (etapa 2) precisa do código para buscar o preço médio — sem ele, cada
 * item seria apagado e cadastrado de novo, levando junto as quantidades.
 *
 * ## O mesmo seletor, dois passos
 *
 * O primeiro é a busca do "Novo item" (`BuscaNoCompras`): só SKU que existe no compras (D9).
 * Com o compras fora do ar — ou sem catálogo nenhum — abre o campo digitado, e o código
 * entra **não conferido**.
 *
 * O segundo mostra o que a escolha significa, **antes** de aplicar: a unidade do compras ao
 * lado da do item (D6), o preço médio dos últimos 12 meses, só para conferir (nada é
 * guardado), e os itens de mesma descrição sem código que podem receber o mesmo (RN-396).
 * Aplicado o código conferido, a foto do preço é tirada à parte (D5, `onCodigoConferido`).
 *
 * Mão de obra não tem código (RN-252): o botão não aparece nela.
 */
export function CodigoDoItem({
  item,
  servico,
  catalogoDaObra,
  dispatch,
  onCodigoConferido,
}: {
  item: CatalogItem;
  servico: CatalogService;
  /**
   * O catálogo **da obra**, e não o recorte da tipologia: o mesmo material sem código pode
   * estar num serviço que esta tipologia não escolheu, e a consolidação da obra o soma.
   */
  catalogoDaObra: Pick<ServiceCatalog, 'disciplines'>;
  dispatch: React.Dispatch<WorkspaceAction>;
  /**
   * O código conferido no compras foi aplicado (Fase 29, D5): é a hora de tirar a foto do
   * preço dele, para o material já nascer com o médio. Chamado **depois** do despacho, e sem
   * esperar: a troca do código não depende do compras responder.
   */
  onCodigoConferido?: (code: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  if (item.kind === 'MAO_DE_OBRA') return null;

  const rotulo = item.code === undefined ? 'Informar código' : 'Trocar código';
  return (
    <>
      <button
        type="button"
        className="text-xs font-medium text-(--color-brand) underline-offset-4 hover:underline"
        aria-label={`${rotulo} de ${item.description}`}
        onClick={() => setAberto(true)}
      >
        {rotulo}
      </button>
      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent
          onEscapeKeyDown={(event) => {
            // O Esc com a lista do catálogo aberta fecha a lista, e não o diálogo: é o que o
            // seletor promete, e perder a escolha inteira por fechar uma lista seria castigo.
            if (
              event.target instanceof HTMLElement &&
              event.target.getAttribute('role') === 'combobox' &&
              event.target.getAttribute('aria-expanded') === 'true'
            ) {
              event.preventDefault();
            }
          }}
        >
          {/* Montado só aberto: reabrir começa do zero, sem a escolha de quem desistiu. */}
          {aberto && (
            <PassosDoCodigo
              item={item}
              servico={servico}
              catalogoDaObra={catalogoDaObra}
              onAplicar={(acao, conferido) => {
                dispatch(acao);
                setAberto(false);
                if (conferido && acao.type === 'SET_ITEM_CODE' && acao.code !== undefined) {
                  onCodigoConferido?.(acao.code);
                }
              }}
              onCancelar={() => setAberto(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function PassosDoCodigo({
  item,
  servico,
  catalogoDaObra,
  onAplicar,
  onCancelar,
}: {
  item: CatalogItem;
  servico: CatalogService;
  catalogoDaObra: Pick<ServiceCatalog, 'disciplines'>;
  /** `conferido`: o código saiu da busca do compras, e não do campo digitado (D9). */
  onAplicar: (acao: WorkspaceAction, conferido: boolean) => void;
  onCancelar: () => void;
}) {
  const [escolhido, setEscolhido] = useState<CodigoEscolhido | undefined>(undefined);
  const [indisponivel, setIndisponivel] = useState<CatalogoIndisponivel | undefined>(undefined);
  const [digitado, setDigitado] = useState('');

  const escolherDoCompras = (produto: SupplyProduct): void =>
    setEscolhido({
      code: produto.code,
      description: produto.description,
      ...(produto.unit === undefined ? {} : { unit: produto.unit }),
      conferido: true,
    });

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {item.code === undefined ? 'Informar código de compras' : 'Trocar código de compras'}
        </DialogTitle>
        <DialogDescription>
          {`${item.description} · ${servico.name} — ${
            item.code === undefined ? 'hoje sem código' : `hoje ${item.code}`
          }`}
        </DialogDescription>
      </DialogHeader>

      {escolhido === undefined ? (
        <>
          <div className="flex flex-col gap-3">
            <BuscaNoCompras
              abertaDeInicio
              onEscolher={escolherDoCompras}
              // Só SKU que existe (D9): não achar é procurar de outro jeito, e não digitar.
              dicaSemAchado="Procure por outro nome, ou pelo código."
              dicaNaFalha="Digite o código abaixo: ele entra como não conferido."
              onIndisponivel={setIndisponivel}
            />

            {indisponivel !== undefined && (
              <form
                className="flex flex-col gap-1.5"
                onSubmit={(event) => {
                  event.preventDefault();
                  const codigo = digitado.trim();
                  if (codigo !== '') setEscolhido({ code: codigo, conferido: false });
                }}
              >
                {indisponivel === 'SEM_CATALOGO' && (
                  <p className="text-xs text-(--color-content-muted)">
                    Sem o catálogo de compras, digite o código: ele entra como não conferido.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    aria-label="Código de compras digitado"
                    placeholder="E-685110"
                    className="h-9 w-40 font-mono"
                    value={digitado}
                    onChange={(event) => setDigitado(event.target.value)}
                  />
                  <Badge tone="warn">não conferido</Badge>
                  <Button
                    type="submit"
                    size="sm"
                    variant="secondary"
                    disabled={digitado.trim() === ''}
                  >
                    Usar este código
                  </Button>
                </div>
              </form>
            )}
          </div>

          <DialogFooter>
            {item.code !== undefined && (
              <Button
                variant="secondary"
                onClick={() =>
                  // Tirar é do item, e só dele: os outros com o mesmo código são decisões que
                  // esta pergunta não fez.
                  onAplicar(
                    { type: 'SET_ITEM_CODE', serviceKey: servico.key, itemKey: item.key },
                    false,
                  )
                }
              >
                Remover código
              </Button>
            )}
            <Button variant="ghost" onClick={onCancelar}>
              Cancelar
            </Button>
          </DialogFooter>
        </>
      ) : (
        <ConfirmarCodigo
          item={item}
          servico={servico}
          catalogoDaObra={catalogoDaObra}
          escolhido={escolhido}
          onVoltar={() => setEscolhido(undefined)}
          onAplicar={onAplicar}
        />
      )}
    </>
  );
}

/**
 * O segundo passo: o que a escolha significa, e a pergunta de propagar (RN-396).
 *
 * "Aplicar a todos" é o padrão (D10) e recebe o foco: o caso comum é a mesma tábua lançada
 * em cinco serviços, e dar código a uma só a separaria das outras na consolidação — duas
 * linhas de compra para a mesma tábua. "Só este item" fica ao lado, para a exceção.
 */
function ConfirmarCodigo({
  item,
  servico,
  catalogoDaObra,
  escolhido,
  onVoltar,
  onAplicar,
}: {
  item: CatalogItem;
  servico: CatalogService;
  catalogoDaObra: Pick<ServiceCatalog, 'disciplines'>;
  escolhido: CodigoEscolhido;
  onVoltar: () => void;
  onAplicar: (acao: WorkspaceAction, conferido: boolean) => void;
}) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  // O período nasce uma vez com o passo: virar a meia-noite com o diálogo aberto não deve
  // refazer a pergunta ao compras.
  const [periodo] = useState(() => periodoDeDozeMeses(new Date()));

  // `async` de propósito, como na sugestão de produtividade: a falha do transporte vira
  // rejeição, que a leitura viva já trata, e não uma exceção solta no diálogo.
  const resposta = useLive(async () => {
    if (session === undefined || activeProjectId === undefined) return undefined;
    return container.getSupplyAveragePrices.execute(
      { projectId: activeProjectId, codes: [escolhido.code], from: periodo.from, to: periodo.to },
      session,
    );
  }, [session?.userId, activeProjectId, escolhido.code, periodo.from, periodo.to]);

  const medias = resposta?.ok === true ? resposta.value : undefined;
  const textoDaMedia = medias === undefined ? undefined : textoDoPrecoMedio(medias, escolhido.code);
  // A unidade da busca vale mais; a do preço médio socorre o código digitado.
  const unidade = compararUnidades(
    escolhido.unit ?? (medias === undefined ? undefined : unidadeNaMedia(medias, escolhido.code)),
    item.unit,
  );
  const repetido = outroItemComCodigo(servico, item.key, escolhido.code);
  const candidatos =
    repetido === undefined
      ? itensParaPropagarCodigo(catalogoDaObra, servico.key, item.key, escolhido.code)
      : [];

  const aplicar = (aTodos: boolean): void =>
    onAplicar(
      {
        type: 'SET_ITEM_CODE',
        serviceKey: servico.key,
        itemKey: item.key,
        code: escolhido.code,
        ...(aTodos
          ? {
              alsoTo: candidatos.map((alvo) => ({
                serviceKey: alvo.serviceKey,
                itemKey: alvo.itemKey,
              })),
            }
          : {}),
      },
      escolhido.conferido,
    );

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5 rounded-md border border-(--color-border-subtle) px-3 py-2">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-mono font-medium">{escolhido.code}</span>
            {!escolhido.conferido && <Badge tone="warn">não conferido</Badge>}
          </p>
          {escolhido.description !== undefined && (
            <p className="text-xs text-(--color-content-muted)">{escolhido.description}</p>
          )}
          {!escolhido.conferido && (
            <p className="text-xs text-(--color-content-muted)">
              Digitado sem o catálogo de compras: ninguém conferiu que este código existe lá.
            </p>
          )}
        </div>

        {unidade !== undefined && (
          <p
            className={cn(
              'text-xs',
              unidade.diferente ? 'text-(--color-state-warn)' : 'text-(--color-content-muted)',
            )}
          >
            {unidade.texto}
          </p>
        )}

        {/*
          O preço médio, só para conferir a escolha — nada é guardado aqui. Lista vazia é
          "não há integração", e não se diz nada; a recusa é dita, porque quem confere precisa
          saber que não houve resposta, e não que não houve preço.
        */}
        {resposta === undefined ? (
          session !== undefined && activeProjectId !== undefined ? (
            <p className="text-xs text-(--color-content-muted)" role="status">
              Consultando o preço médio no compras…
            </p>
          ) : null
        ) : !resposta.ok ? (
          <p className="text-xs text-(--color-content-muted)">
            {`Preço médio indisponível: ${resposta.error.message}`}
          </p>
        ) : textoDaMedia === undefined ? null : (
          <p data-preco-medio className="tabular text-xs">
            {textoDaMedia}
          </p>
        )}

        {repetido !== undefined && (
          <p className="text-xs text-(--color-state-warn)">
            {`Este serviço já tem um item com o código ${escolhido.code}: ${repetido.description}.`}
          </p>
        )}

        {candidatos.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="text-sm">
              {`${item.description} aparece em mais ${String(candidatos.length)} ${
                candidatos.length === 1 ? 'serviço' : 'serviços'
              } sem código. Aplicar ${escolhido.code} a todos?`}
            </p>
            <ul
              aria-label="Itens de mesma descrição sem código"
              className="flex max-h-40 flex-col gap-0.5 overflow-y-auto text-xs text-(--color-content-muted)"
            >
              {candidatos.map((alvo) => (
                <li key={`${alvo.serviceKey}»${alvo.itemKey}`}>
                  {`${alvo.disciplineName} › ${alvo.serviceName}`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onVoltar}>
          Escolher outro
        </Button>
        {candidatos.length > 0 ? (
          <>
            <Button variant="secondary" onClick={() => aplicar(false)}>
              Só este item
            </Button>
            <Button variant="primary" autoFocus onClick={() => aplicar(true)}>
              Aplicar a todos
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            autoFocus
            disabled={repetido !== undefined}
            onClick={() => aplicar(false)}
          >
            Aplicar código
          </Button>
        )}
      </DialogFooter>
    </>
  );
}
